import { cn } from "~/lib/utils";
import { ImageGalleryDialog } from "../common/ImageGalleryDialog";
import { useMemo, memo, useState } from "react";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";

const getImageKitUrlEndpoint = (): string => {
  return process.env.PUBLIC_REMIX_APP_IMAGEKIT_URL_ENDPOINT || "https://ik.imagekit.io/officehub";
};

interface GalleryImage {
  image: string;
  isBuilding: boolean;
  order: number;
  url: string;
}

const normalizePath = (path: string) => path.replace(/^\/+/, "");

const withImageKitTransform = (path: string, transform: string) =>
  `https://ik.imagekit.io/officehub/${transform}/${normalizePath(path)}`;

const FALLBACK_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

function ResilientImage({
  src,
  fallbackSrc,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & { fallbackSrc: string }) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasRetried, setHasRetried] = useState(false);

  return (
    <img
      {...props}
      src={currentSrc}
      onError={() => {
        if (!hasRetried) {
          setHasRetried(true);
          setCurrentSrc(fallbackSrc);
          return;
        }
        setCurrentSrc(FALLBACK_PIXEL);
      }}
    />
  );
}

const GridHeroBanner = memo(function GridHeroBanner({
  assets,
  liked,
  toggleFavorite,
  propertyId,
  buildingId,
  videoUrl,
  listings,
  listingId,
  isBuildingPage,
  isPropertyPage,
  isBlogPage,
  altText,
  titleText
}: {
  assets: GalleryImage[];
  liked?: boolean;
  toggleFavorite?: () => void;
  listings?: any;
  videoUrl?: string;
  buildingId?: string;
  propertyId?: string;
  listingId?: string | number;
  isBuildingPage?: boolean;
  isPropertyPage?: boolean;
  altText?: string;
}) {
  const safeAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((item) => item?.url) : []),
    [assets],
  );
  const [firstImage] = safeAssets;
  const safeAltText = altText || "Gallery image";
  const displayImages = useMemo(() => {
    const images: GalleryImage[] = [];
    const secondary = safeAssets.slice(1);
    const pool = secondary.length ? secondary : safeAssets;

    for (let i = 0; i < 4; i++) {
      if (secondary[i]) {
        images.push(secondary[i]);
      } else if (pool.length) {
        images.push(pool[i % pool.length]);
      }
    }

    return images;
  }, [safeAssets]);

  if (!safeAssets.length || !firstImage?.url) {
    return (
      <div id={PAGE_SECTION_IDS.hero} className={`relative ${PAGE_SECTION_SCROLL_CLASS}`} aria-label="gallery-trigger" role="button">
        <div className='overflow-hidden rounded-lg h-[360px] bg-gray-100 flex items-center justify-center text-gray-500'>
          No images available
        </div>
      </div>
    );
  }


  return (
    <section id={PAGE_SECTION_IDS.hero} className={PAGE_SECTION_SCROLL_CLASS}>
    <ImageGalleryDialog
      images={safeAssets}
      liked={liked}
      data={listings}
      videoUrl={videoUrl}
      toggleFavorite={toggleFavorite}
      isBuildingPage={isBuildingPage}
      isPropertyPage={isPropertyPage}
      isBlogPage={isBlogPage}
      listingId={listingId}
      altText={safeAltText}
      trigger={
        <div className='relative' aria-label="gallery-trigger" role="button">
          <div className='overflow-hidden rounded-lg h-[360px]'>
            {assets.length <= 1 ? (
              // Single image view - Use native img for LCP optimization
              <div className='w-full h-full shadow-custom rounded-lg border-2 border-gray-100 flex items-center justify-center'>
                <div className='relative h-full cursor-pointer overflow-hidden rounded-lg bg-gray-200 flex items-center justify-center'>
                  <img
                    src={withImageKitTransform(firstImage.url, "tr:fo-auto")}
                    sizes="(max-width: 768px) 100vw, 2400px"
                    alt={`${altText} - Image ${firstImage.order}`}
                    title={titleText}
                    width={2400}
                    height={720}
                    loading="eager"
                    decoding="sync"
                    fetchPriority="high"
                    className='object-contain max-w-full max-h-full'
                    style={{ contentVisibility: "auto" }}
                  />
                </div>
              </div>
            ) : (
              // Multiple images grid view - Use native img for LCP optimization
              <div className='flex h-full gap-1'>
                <div className='w-[calc(50%-2px)] h-full'>
                  <div className='relative h-full cursor-pointer overflow-hidden bg-gray-200'>
                    <img
                      src={withImageKitTransform(firstImage.url, "tr:w-1600,h-720,fo-auto")}
                      srcSet={`
                        ${withImageKitTransform(firstImage.url, "tr:w-1200,h-540,fo-auto")} 1200w,
                        ${withImageKitTransform(firstImage.url, "tr:w-1600,h-720,fo-auto")} 1600w,
                        ${withImageKitTransform(firstImage.url, "tr:w-2000,h-900,fo-auto")} 2000w
                      `}
                      sizes="(max-width: 768px) 50vw, 1200px"
                      alt={`${altText} - Image ${firstImage.order}`}
                      title={titleText}
                      width={1600}
                      height={720}
                      loading="eager"
                      decoding="sync"
                      fetchPriority="high"
                      className='object-cover w-full h-full'
                      style={{ contentVisibility: "auto" }}
                    />
                  </div>
                </div>
                <div className='w-[calc(50%-2px)] h-full'>
                  <div className='grid grid-cols-1 grid-rows-2 md:grid-cols-2 md:grid-rows-2 gap-1 h-full'>
                    {displayImages.map((image: GalleryImage, index) => (
                      <div
                        key={`${image.order}-${index}`}
                        className={cn(
                          "relative cursor-pointer overflow-hidden h-full",
                          index >= 2 ? "hidden md:block" : "h-full",
                        )}
                      >
                        <ResilientImage
                          src={withImageKitTransform(image.url, "tr:w-700,h-360,fo-auto")}
                          fallbackSrc={withImageKitTransform(image.url, "tr:fo-auto")}
                          alt={`${safeAltText} - Image ${image.order}`}
                          width={350}
                          height={180}
                          title={titleText}
                          loading={index === 0 ? "eager" : "lazy"}
                          fetchPriority={index === 0 ? "high" : "auto"}
                          className='object-cover w-full h-full'
                          decoding='async'
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {safeAssets.length > 3 && (
            <div
              className={cn(
                "absolute z-[1] bottom-[12px]",
                toggleFavorite
                  ? "right-[0rem] sm:bottom-[8px] sm:right-[8px]"
                  : "right-[8px] sm:right-[8px]",
              )}
            >
              <button className='inline-flex items-center justify-center rounded-md text-xs lg:text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10  px-2 py-1 lg:px-4 lg:py-2'>
                Show All Photos ({safeAssets.length})
              </button>
            </div>
          )}
        </div>
      }
    />
    </section>
  );
});

GridHeroBanner.displayName = "GridHeroBanner";

export default GridHeroBanner;
